import React from "react";
import { useQueryClient } from "@tanstack/react-query";

import { API, MCP_SERVERS } from "config";
import * as types from "types";
import type { McpConfig } from "types/Mcp";
import { makeChatStreamParser } from "../methods/parseChatStream";
import { prependSystemPrompt } from "../methods/prependSystemPrompt";
import { isSubagentEnabled } from "../../../components/ThemeSelector/useSubagentState";
import { isLocalOrSandbox } from "../../../utilities/environment";
import {
  MEMORIES_QUERY_KEY,
  MEMORY_TOOL_NAMES,
  isMemoryEnabled,
} from "utilities/memory";
import useGetMetricsToken from "../../metricsApi/hooks/useGetMetricsToken";
import useUser from "../../../components/Login/useUser";
import { makeChatRecorder } from "services/metricsApi/utils/makeChatRecorder";
import {
  type ActiveStream,
  deleteActiveStream,
  getActiveStream,
  hasActiveStream,
  setActiveStream,
} from "./activeStreams";

const baseUrl = API.cortevaChain.url;

function isCompactionFailed(
  progressMessage: types.CompactionProgressMessage | undefined
): progressMessage is types.CompactionProgressMessage {
  return (
    progressMessage !== undefined &&
    (progressMessage.phase === "failed" || progressMessage.phase === "skipped")
  );
}

/**
 * Mutable slot for each thread's setStreamState dispatcher.
 * XHR callbacks call through this so the currently-mounted component
 * (if any) receives state updates. When no component is mounted the
 * entry is absent and updates are silently dropped.
 */
const streamStateDispatchers = new Map<
  string,
  React.Dispatch<React.SetStateAction<StreamState>>
>();

/**
 * Mutable slot for each thread's onAbort callback.
 * Only meaningful when a component is mounted (user-initiated stop).
 */
const abortCallbacks = new Map<
  string,
  ((content: string) => void) | undefined
>();

/**
 * This function attempts to retain the old support for APIs with chat_completion
 * endpoints that emit raw text server-sent event messages rather than `data` events.
 *
 * Raw text:
 *
 * ```
 * Good news!
 *
 * I will definitely never start a stream with 'data:'.
 * ```
 *
 * Data events:
 *
 * ```
 * data: {"choices": [{"delta": {"content": "Good news!"}}]}
 * ```
 */
function handlePlainTextStream(
  responseText: string,
  setMessages: React.Dispatch<React.SetStateAction<types.Message[]>>
) {
  setMessages((curr) => {
    const newCurr = [...curr];

    const oldLastMessage = curr[curr.length - 1];

    let newLastMessage = { ...oldLastMessage };
    newLastMessage.content = responseText;

    if (oldLastMessage.role !== "assistant") {
      // We need to add a new message.
      newLastMessage.role = "assistant";
      newLastMessage.uuid = crypto.randomUUID();
      newCurr.push(newLastMessage);
    } else {
      // Update existing message.
      newCurr[newCurr.length - 1] = newLastMessage;
    }

    return newCurr;
  });
}

export type StreamStatus = "init" | "loading" | "error" | "done" | "aborted";

export interface StreamState {
  status: StreamStatus;
  /**
   * Will be defined when the stream encountered an error (i.e. status is "error")
   */
  error?: Error;
  /**
   * Will be defined when the stream has finished (i.e. status is "done")
   */
  data?: string;
  /**
   * Count of turns that have ended on this thread.
   *
   * Ending does not always change `status`: a second failure goes from `error`
   * to `error`, so a consumer watching only the status string sees no transition
   * and does not re-run. The injection drain depends on observing every end,
   * so this gives it a dependency that always changes. Lives in `StreamState`
   * (rather than a separate `useState`) so it routes through the same
   * `streamStateDispatchers` map, which already handles delivery to whichever
   * component is currently mounted for the thread.
   */
  turnsEnded: number;
}

export interface ChatStreamHandle extends StreamState {
  /**
   * Aborts the in-flight request and discards any partial assistant message.
   */
  abort: () => void;
  /** See the implementation's comment: reads `activeStreams`, not `xhrRef`. */
  isTurnInFlight: () => boolean;
}

interface useChatStreamOptions {
  /**
   * Necessary when dealing with files, seems like the backend uses chatThreadId
   * in uploaded file paths.
   */
  chatThreadId: string;
  persona: types.Persona;
  messages: types.Message[];
  setMessages: React.Dispatch<React.SetStateAction<types.Message[]>>;
  getAccessToken: () => Promise<string>;
  /**
   * Called when the stream is aborted. Receives the content of the user message
   * that was removed from history so the caller can restore it to the draft.
   */
  onAbort?: (userMessageContent: string) => void;
  /**
   * Called when the stream is ended.
   */
  onStreamEnd?: (finalMessages: types.Message[]) => void;
  /**
   * When true, strip all tools from the outgoing request. Used by IncognitoChat
   * so file, code-execution, and subagent tools are unavailable to the model.
   * See ADR 057.
   */
  incognito?: boolean;
}

export default function useChatStream({
  chatThreadId,
  persona,
  messages,
  setMessages,
  getAccessToken,
  onAbort,
  onStreamEnd,
  incognito = false,
}: useChatStreamOptions) {
  const queryClient = useQueryClient();
  const [streamState, setStreamState] = React.useState<StreamState>(() =>
    hasActiveStream(chatThreadId)
      ? { status: "loading", turnsEnded: 0 }
      : { status: "init", turnsEnded: 0 }
  );
  const xhrRef = React.useRef<XMLHttpRequest | null>(null);
  // Tracks the UUID of the user message that last triggered a request. Used to
  // suppress re-triggers after an abort strips both the assistant and user messages,
  // which would otherwise leave the prior user message as the last message again.
  const lastRequestedUserMessageIdRef = React.useRef<string | null>(null);
  // Stores the full user message that triggered the current request so its content
  // can be returned to the caller on abort.
  const lastRequestedUserMessageRef = React.useRef<types.Message | null>(null);

  const abort = React.useCallback(() => {
    const stream = getActiveStream(chatThreadId);
    if (stream) {
      stream.userAborted = true;
      stream.xhr.abort();
    }
  }, [chatThreadId]);

  /**
   * True while a turn is in flight for this thread.
   *
   * Reads the module-level `activeStreams` map rather than the per-mount
   * `xhrRef`: the adoption effect below copies a background stream's xhr into a
   * fresh mount's ref, but that stream's own handlers only ever clear the ref of
   * the mount that created it, so a ref-based answer is wrong after a
   * thread-switch-and-return. The map entry is written synchronously with the
   * XHR construction, *before* the `loading` dispatch that follows
   * `await getAccessToken()`, so a caller that appends a message can observe the
   * resulting turn on the very next commit. That window is why the injection
   * drain needs this rather than `status` alone.
   */
  const isTurnInFlight = React.useCallback(
    () => hasActiveStream(chatThreadId),
    [chatThreadId]
  );

  // Register dispatchers so background XHR callbacks reach the mounted component.
  React.useEffect(() => {
    streamStateDispatchers.set(chatThreadId, setStreamState);
    abortCallbacks.set(chatThreadId, onAbort);
    return () => {
      streamStateDispatchers.delete(chatThreadId);
      abortCallbacks.delete(chatThreadId);
    };
  }, [chatThreadId, setStreamState, onAbort]);

  // Adopt an existing background stream on mount.
  React.useEffect(() => {
    const existing = getActiveStream(chatThreadId);
    if (existing) {
      xhrRef.current = existing.xhr;
      lastRequestedUserMessageIdRef.current =
        existing.lastRequestedUserMessageId;
      lastRequestedUserMessageRef.current = existing.lastRequestedUserMessage;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMetricsToken = useGetMetricsToken();
  const user = useUser();
  const username = user?.username;

  React.useEffect(() => {
    if (messages.length === 0) {
      return;
    }

    // Only request a new message from OpenAI if the last message was from the user.
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== "user") {
      return;
    }

    // Suppress re-triggers caused by an abort stripping the partial assistant
    // message: the same user message UUID would be the last message again.
    // A genuinely new send will always have a different UUID.
    if (lastMessage.uuid === lastRequestedUserMessageIdRef.current) {
      return;
    }

    // Do not start a new request if one already exists. Reads the shared
    // `activeStreams` map rather than `xhrRef` so this and `isTurnInFlight`
    // cannot disagree, and so a mount that adopted a background stream is not
    // wedged forever: the adopted ref is never cleared by that stream's own
    // handlers, which previously made every later send on that mount a silent
    // no-op until remount.
    if (hasActiveStream(chatThreadId)) {
      return;
    }

    lastRequestedUserMessageIdRef.current = lastMessage.uuid;
    lastRequestedUserMessageRef.current = lastMessage;
    xhrRef.current = new XMLHttpRequest();
    const activeStream: ActiveStream = {
      xhr: xhrRef.current,
      lastRequestedUserMessageId: lastMessage.uuid,
      lastRequestedUserMessage: lastMessage,
      userAborted: false,
    };
    setActiveStream(chatThreadId, activeStream);

    const parseChatStream = makeChatStreamParser(messages);

    const fetchData = async () => {
      const accessToken = await getAccessToken();
      const chatRecorder = makeChatRecorder(getMetricsToken);

      // Based on https://github.com/openai/openai-node/issues/18#issuecomment-1490970390
      // Functional update so `turnsEnded` survives. `error`/`data` are cleared
      // explicitly, which the previous full replace did implicitly.
      streamStateDispatchers.get(chatThreadId)?.((prev) => ({
        status: "loading",
        turnsEnded: prev.turnsEnded,
        error: undefined,
        data: undefined,
      }));
      const xhr = xhrRef.current;
      if (xhr === null) return;

      const url = `${baseUrl}/chat/completions`;

      xhr.open("POST", url);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);

      xhr.onprogress = (_event: ProgressEvent<EventTarget>) => {
        // For historical reasons, responses from the CC API can either be plain
        // text or server-sent events.
        // We have to handle these differently.
        const contentType = xhr.getResponseHeader("Content-Type");
        const isPlainText = contentType?.includes("text/plain");
        const responseText = xhr.responseText;
        if (isPlainText) {
          handlePlainTextStream(responseText, setMessages);
          return;
        }
        // Some backends declare content type `text/event-stream` but produce
        // plaintext. Sniff the first line to decide which parser to use.
        const isSSE = contentType?.includes("text/event-stream");
        const looksLikeSSE =
          responseText.startsWith("data: ") ||
          responseText.startsWith("event: ");
        if (isSSE && !looksLikeSSE) {
          handlePlainTextStream(responseText, setMessages);
          return;
        }

        // Assume we have a stream of data events.
        setMessages(parseChatStream(responseText));
      };

      xhr.onabort = () => {
        streamStateDispatchers.get(chatThreadId)?.((prev) => ({
          ...prev,
          status: "aborted",
          turnsEnded: prev.turnsEnded + 1,
        }));
        xhrRef.current = null;
        deleteActiveStream(chatThreadId);
        const abortedUserMessage = lastRequestedUserMessageRef.current;
        // Remove the partial assistant message (if any) and the user message that
        // triggered the request, leaving history clean for any subsequent send.
        setMessages((prev) => {
          // Strip the transient compaction progress message first — it can sit at the
          // end of the array and prevent the checks below from reaching the user message.
          let next = prev.filter(
            (msg) => !types.isCompactionProgressMessage(msg)
          );
          if (next[next.length - 1]?.role === "assistant") {
            next = next.slice(0, -1);
          }
          if (
            abortedUserMessage &&
            next[next.length - 1]?.uuid === abortedUserMessage.uuid
          ) {
            next = next.slice(0, -1);
          }
          return next;
        });
        const callback = abortCallbacks.get(chatThreadId);
        if (abortedUserMessage && callback) {
          callback(abortedUserMessage.content);
        }
      };

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            streamStateDispatchers.get(chatThreadId)?.((prev) => ({
              status: "done",
              data: xhr.responseText,
              turnsEnded: prev.turnsEnded + 1,
            }));

            // Invalidate cached previews for any file edited in-place during
            // this stream. edit_file overwrites S3 at the same key, so the
            // permalink is unchanged, so staleTime:Infinity would otherwise
            // serve stale bytes on the next eye-icon click.
            // Scan the raw response text rather than doing this inside a
            // setMessages updater, which must be a pure function.
            let memoryChanged = false;
            xhr.responseText.split("\n").forEach((line) => {
              if (!line.startsWith("data: ")) return;
              try {
                const parsed = JSON.parse(line.slice(6));
                if (
                  parsed.type === "tool_result" &&
                  MEMORY_TOOL_NAMES.includes(parsed.tool_name)
                ) {
                  memoryChanged = true;
                }
                // TODO: Clean this up!!
                if (
                  parsed.type === "tool_result" &&
                  (parsed.tool_name === "edit_file" ||
                    (typeof parsed.tool_name === "string" &&
                      parsed.tool_name.endsWith("_edit_file"))) &&
                  !parsed.error
                ) {
                  const content =
                    typeof parsed.content === "string"
                      ? parsed.content
                      : JSON.stringify(parsed.content);
                  const result = JSON.parse(content);
                  // Match on variant_id extracted from the permalink path rather
                  // than the full URL: the tool result uses the internal hostname
                  // (ccf-api:8002) while the query key holds the public-facing URL.
                  const variantId = result?.permalink?.match(
                    /\/variant\/([^/]+)\/permalink/
                  )?.[1];
                  if (variantId) {
                    queryClient.invalidateQueries({
                      queryKey: ["ctvaChain", "downloadFile"],
                      predicate: (query) =>
                        typeof query.queryKey[4] === "string" &&
                        query.queryKey[4].includes(variantId),
                    });
                  }
                }
              } catch {
                // unparseable line, skip
              }
            });
            // The agent changed memories, so the memory view refetches (UI ADR 078).
            if (memoryChanged) {
              queryClient.invalidateQueries({ queryKey: MEMORIES_QUERY_KEY });
            }

            // Remove progress events since the stream is over.
            // Also handle empty responses by adding a synthetic assistant message.
            setMessages((prevMessages) => {
              const progressMessage = prevMessages.find(
                types.isCompactionProgressMessage
              );
              const compactionFailed = isCompactionFailed(progressMessage);
              // For Datadog to capture
              if (compactionFailed) {
                console.error(
                  `Compaction ${progressMessage.phase}: ${
                    progressMessage.reason ?? "unknown reason"
                  }`
                );
              }

              let nextMessages = prevMessages.filter(
                (message) => !types.isCompactionProgressMessage(message)
              );

              nextMessages = nextMessages.map((message) => {
                if (
                  types.isAssistantResponseMessage(message) &&
                  Array.isArray(message.mcpEvents)
                ) {
                  const newMcpEvents = message.mcpEvents.filter(
                    (e) => e.event_type === "log"
                  );
                  return { ...message, mcpEvents: newMcpEvents };
                }

                return message;
              });

              if (compactionFailed) {
                const lastAssistantIndex = nextMessages.findLastIndex(
                  types.isAssistantResponseMessage
                );
                const assistantMessage = nextMessages[lastAssistantIndex];
                if (
                  assistantMessage &&
                  types.isAssistantResponseMessage(assistantMessage)
                ) {
                  nextMessages[lastAssistantIndex] = {
                    ...assistantMessage,
                    compactionFailed: true,
                  };
                }
              }

              // If the last message is still a user message, the API returned
              // no content (e.g., blocked by safety filter). Add a synthetic
              // assistant response to prevent infinite request loops.
              const lastMessage = nextMessages[nextMessages.length - 1];
              if (lastMessage && lastMessage.role === "user") {
                nextMessages = [
                  ...nextMessages,
                  {
                    role: "assistant" as const,
                    uuid: crypto.randomUUID(),
                    content: "The API returned no response.",
                  },
                ];
              }

              if (onStreamEnd !== undefined) {
                onStreamEnd(nextMessages);
              }

              return nextMessages;
            });
            xhrRef.current = null;
            deleteActiveStream(chatThreadId);
          } else if (activeStream.userAborted) {
            // User clicked stop. xhr.onabort already cleaned up history;
            // suppress the error log so the stop-button click doesn't
            // surface a red herring in the console. A genuine network
            // failure (also status 0) keeps falling into the error branch
            // below so it stays visible.
            //
            // Release anyway: aborting an *unsent* XHR fires no `onabort` event
            // per spec, so when `abort()` landed during the token fetch this is
            // the only handler that runs. Idempotent when `onabort` did fire.
            xhrRef.current = null;
            deleteActiveStream(chatThreadId);
          } else {
            console.error("Request failed with status " + xhr.status);
            streamStateDispatchers.get(chatThreadId)?.((prev) => ({
              status: "error",
              error: new Error("Request failed with status " + xhr.status),
              turnsEnded: prev.turnsEnded + 1,
            }));
            // Release the in-flight guard, as the success and abort paths do.
            // The request has reached readyState 4, so there is nothing left to
            // duplicate; leaving the ref set makes the `xhrRef.current !== null`
            // check at the top of this effect reject every later request, so one
            // failed stream disabled the chat (and stalled the long-task
            // injection drain) until the component remounted.
            //
            // This does not become a retry loop: the error path deliberately
            // leaves the message list alone, so the failed user message is still
            // last and the last-requested-uuid guard suppresses a re-trigger.
            // Only a genuinely new send proceeds.
            xhrRef.current = null;
            deleteActiveStream(chatThreadId);
          }

          chatRecorder.sendAnalytics(xhr);
        }
      };

      const enableJupyterHub =
        localStorage.getItem("enableJupyterHub")?.toLowerCase() === "true";

      const enableSubagent = isSubagentEnabled();

      // Prepend the system prompt to the messages (with toggle states)
      const messagesWithSystemPrompt = prependSystemPrompt(
        messages,
        persona,
        enableSubagent,
        incognito
      );

      // Progress messages are transient UI indicators. If the previous request
      // failed mid-compaction they can linger in state — strip them so the
      // backend never receives an unknown role.
      const allFormattedMessages = messagesWithSystemPrompt
        .filter((msg) => !types.isCompactionProgressMessage(msg))
        .map((msg) => {
          return types.formatMessageForApi(msg);
        });

      const lastCompactionIndex = allFormattedMessages.findLastIndex(
        (msg) => msg.role === "compaction"
      );
      let formattedMessages: typeof allFormattedMessages;
      if (lastCompactionIndex === -1) {
        formattedMessages = allFormattedMessages;
      } else {
        const systemMessages = allFormattedMessages.filter(
          (msg) => msg.role === "system"
        );
        formattedMessages = [
          ...systemMessages,
          ...allFormattedMessages.slice(lastCompactionIndex),
        ];
      }

      const enableSearch = !persona.isInternal
        ? localStorage.getItem("enableSearch")?.toLowerCase() === "true"
        : false;

      // In incognito mode, send an empty MCP config to strip all tools (ADR 057).
      // Otherwise, merge persona's MCP config with file API and runtime-toggled MCPs.
      let mcpConfig: McpConfig = {};
      if (!incognito) {
        mcpConfig = {
          ...persona.mcpConfig,
          cc_file_api: MCP_SERVERS.cc_file_api,
        };
        // Code execution is always on for capable personas (ADR 074).
        if (persona.codeExecutionCapable !== false) {
          mcpConfig = { ...mcpConfig, executeCode: MCP_SERVERS.executeCode };
        }
        if (
          isLocalOrSandbox() &&
          enableJupyterHub &&
          persona.jupyterHubCapable !== false
        ) {
          mcpConfig = {
            ...mcpConfig,
            jupyterhub_mcp_shim: MCP_SERVERS.jupyterhub_mcp_shim,
          };
        }
        if (enableSubagent && persona.subagentCapable === true) {
          mcpConfig = { ...mcpConfig, subagent: MCP_SERVERS.subagent };
        }
      }

      const apiArgs: any = {
        chat_thread_id: chatThreadId,
        model: persona.modelName,
        persona_id: String(persona.id),
        enable_search: enableSearch,
        // Memory needs a thread and never runs in incognito (UI ADR 078).
        enable_memory: !incognito && isMemoryEnabled(),
        mcp_config: mcpConfig,
        mcp_preload_resources: incognito
          ? undefined
          : persona.mcpPreloadResources,
        messages: formattedMessages,
      };
      if (persona.maxRecursionDepth !== undefined) {
        apiArgs.max_recursion_depth = persona.maxRecursionDepth;
      }
      if (persona.reasoningEffort !== undefined) {
        apiArgs.reasoning_effort = persona.reasoningEffort;
      }
      const requestOptionsString = JSON.stringify(apiArgs);

      chatRecorder.addParams({
        request_url: url,
        chat_thread_id: chatThreadId,
        // Should be defined since we return early if the last message was not
        // from a user.
        last_user_message: messages[messages.length - 1].content,
        model: persona.modelName,
        persona_name: persona.name,
        // Force all persona IDs to be strings.
        // Should be fine since they are either integers or string UUIDs.
        persona_id: String(persona.id),
        enable_search: enableSearch,
        messages: JSON.stringify(messages),
      });
      if (username !== undefined) {
        chatRecorder.addParams({ requested_by: username });
      }
      if (mcpConfig !== undefined) {
        chatRecorder.addParams({ mcp_config: JSON.stringify(mcpConfig) });
      }

      xhr.send(requestOptionsString);
    };

    // `fetchData` awaits `getAccessToken()` before it can attach any handler, so
    // a rejection there (MSAL interaction/refresh failure) means no
    // readyState-4 callback will ever run to clear the in-flight guard. Without
    // this catch the rejection is also unhandled, and the chat silently refuses
    // every later send.
    fetchData().catch((error) => {
      console.error("Chat stream request failed to start:", error);
      streamStateDispatchers.get(chatThreadId)?.((prev) => ({
        status: "error",
        error: error instanceof Error ? error : new Error(String(error)),
        turnsEnded: prev.turnsEnded + 1,
      }));
      xhrRef.current = null;
      deleteActiveStream(chatThreadId);
    });
  }, [
    chatThreadId,
    persona,
    messages,
    username,
    setMessages,
    getAccessToken,
    getMetricsToken,
    onStreamEnd,
    queryClient,
    incognito,
  ]);

  return { ...streamState, abort, isTurnInFlight };
}
